import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Lightbulb, Sparkles, Loader, Brain, ArrowRight } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useSessionStore } from '../store/sessionStore';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../lib/i18n/context';
import { SEOHead } from '../components/SEOHead';
import type { Language } from '../lib/i18n/config';

export function HomePage() {
  const [topic, setTopic] = useState('');
  const [nickname, setNickname] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { lang } = useParams<{ lang: Language }>();
  const { t } = useTranslation();
  const { language } = useLanguage();
  const setSession = useSessionStore((state) => state.setSession);
  const setParticipant = useSessionStore((state) => state.setParticipant);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!topic.trim() || !nickname.trim() || isLoading) return;

    setIsLoading(true);
    setError(null);

    try {
      // Create new session
      const { data: sessionData, error: sessionError } = await supabase
        .from('sessions')
        .insert([
          {
            topic: topic.trim(),
            creator_nickname: nickname.trim(),
            language
          },
        ])
        .select()
        .single();

      if (sessionError) throw sessionError;
      if (!sessionData) throw new Error(t('common.error'));

      // Create participant for creator
      const { data: participantData, error: participantError } = await supabase
        .from('participants')
        .insert([
          {
            session_id: sessionData.id,
            nickname: nickname.trim(),
          },
        ])
        .select()
        .single();

      if (participantError) throw participantError;
      if (!participantData) throw new Error(t('common.error'));

      // Update store and navigate
      setSession(sessionData);
      setParticipant(participantData);
      navigate(`/${language}/session/${sessionData.id}`);
    } catch (error) {
      console.error('Error creating session:', error);
      setError(
        error instanceof Error 
          ? error.message 
          : t('common.error')
      );
      setIsLoading(false);
    }
  };

  return (
    <>
      <SEOHead 
        title={t('home.title') + ' | ABCbrain.xyz'}
        description={t('home.subtitle')}
        canonicalPath={`/${language}`}
      />
      
      <div className="max-w-6xl mx-auto">
        {/* Hero section */}
        <div className="mb-16 text-center">
          <div className="flex justify-center mb-6">
            <div className="relative">
              <div className="absolute -inset-1 bg-gradient-to-r from-primary-500 to-accent-500 rounded-full blur opacity-70"></div>
              <Brain className="relative h-20 w-20 text-primary-500" />
            </div>
          </div>
          <h1 className="text-5xl font-bold text-gray-900 mb-4">
            {t('home.title')}
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            {t('home.subtitle')}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Method explanation */}
          <div className="bg-white rounded-xl p-8 shadow-sm">
            <h2 className="flex items-center text-2xl font-semibold text-gray-900 mb-4">
              <Lightbulb className="h-6 w-6 text-primary-500 mr-2" />
              {t('home.explanation.title')}
            </h2>
            
            <div className="space-y-4">
              <p className="text-gray-700">
                {t('home.explanation.intro')}
              </p>
              
              <div className="pl-4 border-l-4 border-primary-100">
                <ol className="space-y-3">
                  <li className="flex items-start">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-100 text-primary-700 font-bold text-sm flex-shrink-0 mr-2">1</span>
                    <span className="text-gray-700">{t('home.explanation.step1')}</span>
                  </li>
                  <li className="flex items-start">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-100 text-primary-700 font-bold text-sm flex-shrink-0 mr-2">2</span>
                    <span className="text-gray-700">{t('home.explanation.step2')}</span>
                  </li>
                  <li className="flex items-start">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-100 text-primary-700 font-bold text-sm flex-shrink-0 mr-2">3</span>
                    <span className="text-gray-700">{t('home.explanation.step3')}</span>
                  </li>
                </ol>
              </div>
              
              <div className="mt-4 text-right">
                <a 
                  href={`/${language}/die-ABC-Listen-Methode`} 
                  className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium"
                >
                  {t('home.explanation.learn_more')}
                  <ArrowRight className="ml-1 h-4 w-4" />
                </a>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="bg-white rounded-xl p-8 shadow-sm">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">
              {t('home.form.title')}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="rounded-lg bg-red-50 p-4 text-sm text-red-600">
                  {error}
                </div>
              )}

              <div>
                <label htmlFor="topic" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('home.topic.label')}
                </label>
                <input
                  type="text"
                  id="topic"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  placeholder={t('home.topic.placeholder')}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 shadow-sm"
                  required
                  disabled={isLoading}
                />
              </div>

              <div>
                <label htmlFor="nickname" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('home.nickname.label')}
                </label>
                <input
                  type="text"
                  id="nickname"
                  value={nickname}
                  onChange={(e) => setNickname(e.target.value)}
                  placeholder={t('home.nickname.placeholder')}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 shadow-sm"
                  required
                  disabled={isLoading}
                />
              </div>

              <button
                type="submit"
                disabled={isLoading || !topic.trim() || !nickname.trim()}
                className="group relative w-full overflow-hidden rounded-lg bg-gradient-to-br from-accent-500 to-primary-500 p-[2px] transition-all hover:from-accent-600 hover:to-primary-600 active:scale-[0.99] disabled:opacity-70 disabled:cursor-not-allowed"
              >
                <div className="absolute inset-0 bg-[length:200%_100%] bg-gradient-to-r from-accent-500/0 via-white/25 to-accent-500/0 group-hover:animate-shimmer" />
                <div className="relative flex items-center justify-center gap-2 rounded-[6px] bg-gradient-to-br from-accent-500 to-primary-500 px-6 py-3 text-lg font-medium text-white">
                  {isLoading ? (
                    <>
                      <Loader className="h-5 w-5 animate-spin" />
                      <span>{t('common.loading')}</span>
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-5 w-5 transition-transform group-hover:scale-110 group-hover:animate-pulse" />
                      <span>{t('home.start.button')}</span>
                    </>
                  )}
                </div>
              </button>
            </form>
          </div>
        </div>

        {/* Features section */}
        <div className="mt-16 bg-gradient-to-r from-primary-50 to-accent-50 rounded-xl p-8">
          <h2 className="text-2xl font-semibold text-gray-900 text-center mb-8">
            {t('home.features.title')}
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white/80 backdrop-blur-sm rounded-lg p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-primary-100 rounded-lg">
                  <Brain className="h-5 w-5 text-primary-600" />
                </div>
                <h3 className="font-medium text-lg text-gray-900">{t('home.features.method.title')}</h3>
              </div>
              <p className="text-gray-700">
                {t('home.features.method.description')}
              </p>
            </div>
            
            <div className="bg-white/80 backdrop-blur-sm rounded-lg p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-primary-100 rounded-lg">
                  <Sparkles className="h-5 w-5 text-primary-600" />
                </div>
                <h3 className="font-medium text-lg text-gray-900">{t('home.features.ai.title')}</h3>
              </div>
              <p className="text-gray-700">
                {t('home.features.ai.description')}
              </p>
            </div>
            
            <div className="bg-white/80 backdrop-blur-sm rounded-lg p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-primary-100 rounded-lg">
                  <Lightbulb className="h-5 w-5 text-primary-600" />
                </div>
                <h3 className="font-medium text-lg text-gray-900">{t('home.features.collaborate.title')}</h3>
              </div>
              <p className="text-gray-700">
                {t('home.features.collaborate.description')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}